<template>
  <div class="checkout-domains-config" v-can-enable="'custom_domain:write'">
    <!-- List customDomains -->
    <el-table :data="customDomainsTable" :default-expand-all="true" class="mt-4 mb-8" style="width: 100%" v-if="hasCustomDomain">
      <!-- <el-table-column type="expand" class="">
        <template slot-scope="scope">
          <p>test</p>
        </template>
      </el-table-column> -->
      <el-table-column prop="url" label="Domain" width="300">
        <template #default="{ row }">
          <pre class="inline-block">
            {{ row.url }}
          </pre>
        </template>
      </el-table-column>
      <el-table-column prop="verificationStatus" label="Verification Status">
        <template #default="{ row }">
          <p v-if="row.verified" class="bg-green-200 text-green-800 font-semibold px-4 py-1 text-xs rounded-xl inline-block">Verified</p>
          <p v-else-if="row.status === 'Error'" class="bg-red-200 text-red-800 font-semibold px-4 py-1 text-xs rounded-xl inline-block">Error</p>
          <p v-else-if="row.status === 'Verifying'" class="bg-indigo-200 text-indigo-800 font-semibold px-4 py-1 text-xs rounded-xl inline-block">
            Verifying
          </p>
          <p v-else class="bg-indigo-200 text-indigo-800 font-semibold px-4 py-1 text-xs rounded-xl inline-block">Pending</p>
          <p class="ml-3 bg-fuschia-200 text-fuschia-800 font-semibold px-4 py-1 text-xs rounded-xl inline-block" v-if="row.primary && row.verified">
            Primary
          </p>
          <p class="text-xs font-medium pt-1" :class="row.status === 'Error' ? 'text-red-700' : 'text-brand-brown'" v-if="row.statusInformation">
            {{ row.statusInformation }}
          </p>
          <div></div>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="200">
        <template #default="{ row }">
          <div class="flex flex-wrap">
            <!-- Delete button -->
            <div class="ml-auto order-2">
              <el-dropdown trigger="click">
                <div>
                  <el-button type="text" text class="table-dropdown outline-none focus:outline-none">
                    <font-awesome-icon :icon="['fas', 'ellipsis-v']" class="text-brand-brown font-thin text-xs mx-4" />
                  </el-button>
                </div>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :disabled="selectedCheckoutDomain && selectedCheckoutDomain.id === row.id" icon="el-icon-delete">
                    <el-popconfirm
                      icon-color="#f7b200"
                      @onConfirm="removeCustomDomain(row)"
                      :title="'Are you sure you want to delete ' + row.url + '?'"
                      cancel-button-text="Nevermind"
                      confirm-button-text="Delete domain"
                    >
                      <span slot="reference" class="py-2">Delete Domain</span>
                    </el-popconfirm>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>

            <el-button
              v-if="!row.verified"
              :loading="selectedCheckoutDomain && selectedCheckoutDomain.id === row.id"
              icon="el-icon-refresh"
              type="primary"
              size="small"
              @click="openCheckoutVerifyDialog(row)"
              class="flex-grow-0"
            >
              Verify
            </el-button>

            <el-button
              v-if="!row.primary && row.verified"
              :loading="settingPrimary"
              icon="el-icon-check"
              type="primary"
              size="small"
              @click="makePrimary(row)"
              class="flex-grow-0 btn-fuschia"
            >
              Make Primary
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- Dialog: Add Checkout Domain -->
    <el-dialog class="p-0" custom-class="container max-w-xl rounded-lg px-0 py-0 no-padding" :visible.sync="showCheckoutAdd" :append-to-body="true">
      <AddCheckoutDomain :action="addCheckoutDomain" :close="closeCheckoutAddDialog" />
    </el-dialog>

    <!-- Dialog: Verify Checkout Domain -->
    <el-dialog
      class="p-0"
      custom-class="container max-w-2xl rounded-lg px-0 py-0 no-padding"
      :visible.sync="showCheckoutVerify"
      @closed="resetCheckoutSelection"
      :append-to-body="true"
    >
      <VerifyCheckoutDomain :action="addCheckoutDomain" :checkoutDomain="selectedCheckoutDomain" :close="closeCheckoutVerifyDialog" />
    </el-dialog>

    <!-- Dialog: Checkout Page Settings -->
    <el-dialog
      class="p-0"
      custom-class="container max-w-lg rounded-lg px-0 py-0 no-padding"
      :visible.sync="showCheckoutSettings"
      :append-to-body="true"
    >
      <CheckoutSettings :action="updateCheckoutSettings" :close="closeCheckoutSettings" />
    </el-dialog>

    <!-- Add Checkout Domain Button -->
    <div class="mt-4">
      <el-button
        :disabled="!canAddCheckoutDomain"
        @click="showCheckoutAdd = true"
        :type="hasCustomDomain ? 'plain' : 'primary'"
        :class="hasCustomDomain ? 'border-brand-gray-blue border-2 px-6 py-2 rounded-lg' : ''"
      >
        <span :class="hasCustomDomain ? 'text-sm text-brand-blue font-semibold' : ''">Add Checkout Domain</span>
      </el-button>
      <el-button @click="showCheckoutSettings = true" type="primary">Checkout Page Settings</el-button>
      <div v-if="!canAddCheckoutDomain" class="bg-white border-2 border-brand-yellow text-brand-brown mt-4 p-4 rounded-lg" role="alert">
        <p class="font-bold text-sm">You have reached your maximum allowed Checkout Domains ({{ maxCheckoutDomains }}).</p>
        <p class="text-sm font-medium">Keep in mind only the Primary domain will be used.</p>
        <p class="text-sm font-medium">
          You can delete unused domains by clicking the vertical ellipsis button on the right of the domain ->
          <span>
            <font-awesome-icon :icon="['fas', 'ellipsis-v']" class="text-brand-brown font-thin text-xs" />
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { customDomainIsValid, customDomainStatus } from '@/helpers/domain-utils';
import DunningService from '@/api/dunning.service';
import AddCheckoutDomain from '@/components/Dunning/Config/CheckoutAdd.vue';
import VerifyCheckoutDomain from '@/components/Dunning/Config/CheckoutVerify.vue';
import CheckoutSettings from '@/components/Dunning/Config/CheckoutSettings.vue';

export default {
  name: 'CheckoutDomainsConfig',
  components: {
    AddCheckoutDomain,
    VerifyCheckoutDomain,
    CheckoutSettings,
  },
  props: {
    dunningData: {
      type: Object,
      required: true,
    },
    reload: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      maxCheckoutDomains: 3,
      showCheckoutAdd: false,
      showCheckoutVerify: false,
      showCheckoutSettings: false,
      selectedCheckoutDomain: {},
      settingPrimary: false,
      isCheckingCustomDomain: false,
    };
  },
  computed: {
    dunning() {
      return this.dunningData; // dunning object passed in from parent
    },
    customDomains() {
      return this.dunning?.checkoutPage?.customDomains || [];
    },
    customDomainsTable() {
      return this.customDomains.map((customDomain, index) => {
        const { status, statusInformation } = customDomainStatus(customDomain);

        return {
          index,
          id: customDomain.id,
          url: customDomain.url,
          verified: customDomainIsValid(customDomain),
          status,
          statusInformation,
          primary: customDomain.id === this.primaryCheckoutDomain?.id,
        };
      });
    },
    canAddCheckoutDomain() {
      return this.customDomains.length < this.maxCheckoutDomains;
    },
    isCustomDomainVerified() {
      return this.customDomains.some(customDomainIsValid);
    },
    verifiedCustomDomains() {
      return this.customDomains.filter(customDomainIsValid);
    },
    subdomain() {
      return this.dunning?.checkoutPage?.ckSubdomain;
    },
    hasSubdomain() {
      return !!this.subdomain;
    },
    primaryCheckoutDomain() {
      return this.dunning?.checkoutPage?.customDomain;
    },
    hasCustomDomain() {
      return this.customDomains.some((domain) => !!domain?.url);
    },
  },
  methods: {
    async addCheckoutDomain(domain) {
      try {
        await DunningService.configureCheckout(domain);
        await this.reload();
        this.$notify({ title: 'Checkout Domain Added', type: 'success' });
      } catch (error) {
        this.displayError(error);
      }
    },
    async removeCustomDomain(row) {
      try {
        this.selectedCheckoutDomain = this.customDomains[row.index];
        if (row.verified) {
          this.$notify({ title: 'Cannot remove a verified Checkout Domain. Please contact us.', type: 'error' });
          return;
        }
        /*
        if (row.primary) {
          this.$notify({ title: 'Cannot remove the primary Checkout Domain. Please add a new domain and set it to Primary.', type: 'error' });
          return;
        }
        */
        if (this.customDomains.length === 1) {
          this.$notify({ title: 'Cannot remove the only Checkout Domain. Please add a new domain before deleting this one.', type: 'error' });
          return;
        }
        await DunningService.deleteCustomDomain(row.id);
        await this.reload();
        this.$notify({ title: 'Domain Removed', type: 'success' });
      } catch (error) {
        this.displayError(error);
      } finally {
        this.resetCheckoutSelection();
      }
    },

    async openCheckoutVerifyDialog(row) {
      // Select domain
      this.selectedCheckoutDomain = this.customDomains[row.index];

      // Retrieve latest verification status
      const success = await this.checkCustomDomain(this.selectedCheckoutDomain);

      // Show dialog if not verified
      if (success) {
        this.showCheckoutVerify = false;
        this.resetCheckoutSelection();
      } else {
        this.showCheckoutVerify = true;
      }
    },

    async updateCheckoutSettings(settings) {
      // { ckSubdomain, redirectLink }
      try {
        await DunningService.configureCheckout(settings);
        await this.reload();
        this.$notify({ title: 'Checkout Settings Updated', type: 'success' });
      } catch (error) {
        this.displayError(error);
      }
    },

    async makePrimary(domain) {
      this.settingPrimary = true;
      try {
        await DunningService.setPrimary(domain.id);
        await this.reload();
        this.$notify({ title: 'Domain set as primary', type: 'success' });
      } catch (error) {
        this.displayError(error);
      } finally {
        this.settingPrimary = false;
      }
    },

    customDomainIsReady(domainId) {
      const domain = this.customDomains.find((d) => d.id === domainId);
      return customDomainIsValid(domain);
    },
    async checkCustomDomain(domain) {
      let success = false;
      this.isCheckingCustomDomain = true;
      // check DNS records for A record propagation
      try {
        await DunningService.getCustomDomain(domain.id);
        await this.$store.dispatch('SYNC_USER_RECORD');
      } catch (e) {
        this.$message.error('We were unable to check the status of your domain. Please try again later.');
        console.log(e);
        return false;
      } finally {
        if (this.customDomainIsReady(domain.id)) {
          this.$message.success('Your custom domain is resolving and your new SSL has been installed!');
          success = true;
        }
        this.isCheckingCustomDomain = false;
      }
      return success;
    },

    // Checkout dialog helpers:
    closeCheckoutAddDialog() {
      this.showCheckoutAdd = false;
    },
    closeCheckoutVerifyDialog() {
      this.showCheckoutVerify = false;
    },
    closeCheckoutSettings() {
      this.showCheckoutSettings = false;
    },
    resetCheckoutSelection() {
      this.selectedCheckoutDomain = {};
    },
  },
};
</script>

<style>
.el-table pre {
  font-size: 12px;
  white-space: normal !important;
  word-wrap: break-word !important;
}
.el-dialog .el-table pre {
  cursor: pointer;
}
.el-dialog .el-table pre:hover {
  color: black;
}
/* Made the vertical ellipsis a button so it is easier to click
   but the usual methods to hide the outline were not working: */
.table-dropdown {
  --tw-ring-opacity: 0 !important;
}
</style>
